import { useState, useEffect } from 'react';
import { usePageTitle } from '@hooks/usePageTitle';
import ContourBG from '@components/ContourBG';

/* ─── Dark mode (same pattern as Landing) ─── */
const useDarkMode = () => {
  const [isDark, setIsDark] = useState(() =>
    typeof window !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains('dark'))
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return isDark;
};

/* ─── Topographic contour background — the atlas signature ─── */
const SECTIONS = [
  'Who can use Castle Atlas', 'Your account', 'Acceptable use',
  'Intellectual property and user content', 'AI-generated content',
  'Voice Room and simulated conversations', 'Payments and subscriptions',
  'Privacy and data', 'Modifications to the Service and Terms',
  'Disclaimers and limitation of liability', 'Indemnity', 'Dispute resolution',
  'Copyright and DMCA policy', 'Termination', 'General terms', 'Governing law', 'Contact',
];

const Terms = () => {
  usePageTitle('Terms of Service — Castle Atlas');
  const isDark = useDarkMode();

  const c = {
    pageBg: isDark ? '#0e0d0c' : '#faf9f7',
    card:   isDark ? '#1c1917' : '#ffffff',
    border: isDark ? '#2d2825' : '#e8e4df',
    head:   isDark ? '#fafaf9' : '#1c1917',
    body:   isDark ? '#a8a29e' : '#57534e',
    muted:  '#78716c',
    strong: isDark ? '#d6d3d1' : '#44403c',
    dash:   isDark ? '#57534e' : '#d4cdc7',
  };

  const H2 = ({ n, children, id }) => (
    <h2 id={id} style={{ display:'flex',alignItems:'baseline',gap:12,fontFamily:'var(--font-display)',fontSize:19,fontWeight:800,color:c.head,letterSpacing:'-0.01em',margin:'0 0 14px',scrollMarginTop:90 }}>
      <span style={{ fontFamily:'var(--font-mono)',fontSize:12,fontWeight:600,color:'#ea580c',letterSpacing:'0.1em',flexShrink:0 }}>§ {String(n).padStart(2,'0')}</span>
      {children}
    </h2>
  );
  const P = ({ children, mb = 12 }) => (
    <p style={{ fontSize:14,color:c.body,lineHeight:1.75,margin:`0 0 ${mb}px` }}>{children}</p>
  );
  const List = ({ items }) => (
    <ul style={{ listStyle:'none',padding:0,margin:'0 0 12px',display:'flex',flexDirection:'column',gap:9 }}>
      {items.map((item,i) => (
        <li key={i} style={{ display:'flex',gap:10,fontSize:14,color:c.body,lineHeight:1.7 }}>
          <span style={{ color:'#ea580c',flexShrink:0,fontFamily:'var(--font-mono)' }}>—</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
  const Mail = () => (
    <a href="mailto:hello@castleatlas.co" style={{ color:'#ea580c',fontWeight:600,textDecoration:'none' }}>hello@castleatlas.co</a>
  );
  const Section = ({ children }) => (
    <section style={{ borderTop:`1px solid ${c.border}`,paddingTop:32 }}>{children}</section>
  );

  return (
    <div id="main-content" tabIndex={-1} style={{ minHeight:'100vh',background:c.pageBg,fontFamily:'var(--font-body)' }}>
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        .lg1 { animation: fadeUp 0.4s ease-out both; }
        .lg2 { animation: fadeUp 0.4s ease-out 0.08s both; }
        .lg3 { animation: fadeUp 0.4s ease-out 0.16s both; }
        @media(prefers-reduced-motion:reduce){ .lg1,.lg2,.lg3 { animation:none!important; } }
      `}</style>

      {/* ── Header — cartographic ── */}
      <div style={{ position:'relative',overflow:'hidden',borderBottom:`1px solid ${c.border}` }}>
        <ContourBG isDark={isDark}/>
        <div className="lg1" style={{ maxWidth:760,margin:'0 auto',padding:'72px 24px 48px',position:'relative',zIndex:2 }}>
          <div style={{ display:'inline-flex',alignItems:'center',gap:10,marginBottom:16 }}>
            <span style={{ fontFamily:'var(--font-mono)',fontSize:11,fontWeight:600,color:'#ea580c',letterSpacing:'0.14em' }}>RECORDS</span>
            <span style={{ width:22,height:1,background:c.dash,display:'inline-block' }}/>
            <span style={{ fontFamily:'var(--font-mono)',fontSize:11,fontWeight:600,color:c.muted,letterSpacing:'0.14em',textTransform:'uppercase' }}>Terms of Service</span>
          </div>
          <h1 style={{ fontFamily:'var(--font-display)',fontSize:'clamp(2rem,5vw,2.8rem)',fontWeight:900,color:c.head,letterSpacing:'-0.03em',lineHeight:1.08,margin:'0 0 14px' }}>
            The rules of the territory.
          </h1>
          <p style={{ fontFamily:'var(--font-mono)',fontSize:11,color:c.muted,letterSpacing:'0.08em',margin:0 }}>
            LAST UPDATED · MAY 9, 2026
          </p>
        </div>
      </div>

      <div style={{ maxWidth:760,margin:'0 auto',padding:'40px 24px 96px' }}>

        {/* ── Index of sections ── */}
        <div className="lg2" style={{ background:c.card,border:`1px solid ${c.border}`,borderRadius:20,padding:'24px 26px',marginBottom:40 }}>
          <div style={{ fontFamily:'var(--font-mono)',fontSize:10,fontWeight:600,color:c.muted,letterSpacing:'0.14em',marginBottom:14 }}>INDEX OF SECTIONS</div>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:'8px 20px' }}>
            {SECTIONS.map((s,i) => (
              <a key={s} href={`#t${i+1}`} style={{ display:'flex',gap:9,fontSize:13,color:c.body,textDecoration:'none',lineHeight:1.5 }}
                onMouseEnter={e=>e.currentTarget.style.color='#ea580c'} onMouseLeave={e=>e.currentTarget.style.color=c.body}>
                <span style={{ fontFamily:'var(--font-mono)',fontSize:11,color:'#ea580c',flexShrink:0 }}>{String(i+1).padStart(2,'0')}</span>
                {s}
              </a>
            ))}
          </div>
        </div>

        <div className="lg3" style={{ display:'flex',flexDirection:'column',gap:32 }}>

          <p style={{ fontSize:15,color:c.strong,lineHeight:1.8,margin:0 }}>
            Welcome to Castle Atlas. By accessing or using our platform at castleatlas.co ("the Service"),
            you agree to be bound by these Terms of Service ("Terms"). Please read them carefully.
            If you do not agree with any part of these Terms, do not use the Service.
          </p>

          <Section>
            <H2 n={1} id="t1">Who can use Castle Atlas</H2>
            <P mb={0}>
              You must be at least 13 years of age to use Castle Atlas. If you are under 18, you represent
              that your parent or legal guardian has reviewed and agreed to these Terms on your behalf.
              By creating an account, you confirm that all information you provide is accurate, current,
              and complete, and that you have the legal capacity to enter into a binding agreement.
            </P>
          </Section>

          <Section>
            <H2 n={2} id="t2">Your account</H2>
            <P>
              You are responsible for maintaining the confidentiality of your account credentials and for
              all activity that occurs under your account. You agree to notify us immediately at{' '}
              <Mail/> if you become aware of any unauthorized access or use.
            </P>
            <P mb={0}>
              You may not share your account credentials with any third party. Castle Atlas is not liable
              for any loss or damage arising from unauthorized use of your account. We reserve the right
              to suspend or terminate accounts that violate these Terms or that we determine, in our sole
              discretion, pose a risk to the platform or other users.
            </P>
          </Section>

          <Section>
            <H2 n={3} id="t3">Acceptable use</H2>
            <P>You agree not to:</P>
            <List items={[
              'Use the Service for any unlawful purpose or in violation of any applicable law or regulation',
              'Attempt to reverse engineer, decompile, disassemble, or extract source code from the platform',
              'Use automated tools, bots, scrapers, or scripts to access or interact with the Service',
              'Share, sell, rent, or transfer your account credentials or access to any third party',
              'Submit prompts or content designed to generate harmful, abusive, discriminatory, or illegal output',
              'Submit prompts or content that infringe the intellectual property or privacy rights of any third party',
              'Attempt to circumvent, disable, or otherwise interfere with security-related features of the Service',
              'Interfere with or disrupt the integrity or performance of the Service or its underlying infrastructure',
              'Impersonate another person or entity, or misrepresent your affiliation with any person or entity',
              'Harvest, collect, or store personal information about other users without their consent',
            ]}/>
          </Section>

          <Section>
            <H2 n={4} id="t4">Intellectual property and user content</H2>
            <P>
              All content on Castle Atlas — including but not limited to course curriculum, exercises,
              scoring logic, illustrations, brand assets, interface design, and instructional copy — is
              the exclusive property of Castle Atlas and is protected by applicable copyright and
              intellectual property laws. You may not reproduce, distribute, modify, display, or create
              derivative works from any Castle Atlas content without our prior written permission.
            </P>
            <P>
              The prompts you write and submit through the platform remain yours. However, by submitting
              content through the Service, you grant Castle Atlas a worldwide, non-exclusive, royalty-free
              license to store, process, display, and use your submitted content for the limited purposes
              of operating the Service, providing your learning experience, and — in anonymized and
              aggregated form only — improving our scoring systems and educational content. We will never
              sell your prompts or attribute them to you publicly without your explicit consent.
            </P>
            <P>By submitting content, you represent and warrant that:</P>
            <List items={[
              'You are the creator and owner of the content, or have the necessary licenses, rights, and permissions to submit it',
              'Your content does not infringe, violate, or misappropriate any third-party intellectual property, privacy, or publicity right',
              'Your content does not violate any law or regulation',
              'Your content is not objectionable, profane, harassing, threatening, or otherwise inappropriate',
            ]}/>
          </Section>

          <Section>
            <H2 n={5} id="t5">AI-generated content</H2>
            <P>
              Castle Atlas uses third-party AI services, currently including DeepSeek, to generate
              responses and evaluate prompt quality. By using the Service, you acknowledge that:
            </P>
            <List items={[
              'AI-generated responses are provided for educational purposes only and do not constitute professional, legal, medical, financial, or any other form of regulated advice',
              'Scores are estimates of prompt quality against defined criteria and are not guarantees of real-world performance or employability outcomes',
              'AI systems can make errors, produce unexpected outputs, or reflect biases present in their training data',
              'We do not warrant the accuracy, completeness, or fitness of AI-generated content for any particular purpose',
            ]}/>
          </Section>

          <Section>
            <H2 n={6} id="t6">Voice Room and simulated conversations</H2>
            <P mb={0}>
              The Voice Room feature simulates sales and negotiation scenarios using AI-generated audio
              and scripted character responses. All characters, companies, and scenarios are entirely
              fictional. Any resemblance to real persons or organizations is coincidental. Conversations
              in the Voice Room are processed in real time and are not stored beyond the active session
              unless you explicitly save them. This feature is provided for training purposes only and
              should not be used to prepare for or conduct actual negotiations without independent
              professional judgment.
            </P>
          </Section>

          <Section>
            <H2 n={7} id="t7">Payments and subscriptions</H2>
            <P>
              Phase 1 of Castle Atlas is free and does not require payment information. Future paid tiers
              may be introduced. When paid features become available:
            </P>
            <List items={[
              'Payments will be processed securely via Stripe; Castle Atlas does not store your financial account details',
              'Subscription fees will be charged on a recurring basis (monthly or annual) as specified at the time of purchase',
              'You may cancel your subscription at any time from your account settings; cancellation takes effect at the end of the current billing period',
              'No refunds are provided for partial billing periods unless required by applicable law in your jurisdiction',
              'We reserve the right to change pricing with 30 days notice to active subscribers',
            ]}/>
          </Section>

          <Section>
            <H2 n={8} id="t8">Privacy and data</H2>
            <P mb={0}>
              Your use of Castle Atlas is governed by our{' '}
              <a href="/privacy" style={{ color:'#ea580c',fontWeight:600,textDecoration:'none' }}>Privacy Policy</a>,
              which is incorporated into these Terms by reference. By using the Service, you consent to
              the collection and processing of your information as described therein.
            </P>
          </Section>

          <Section>
            <H2 n={9} id="t9">Modifications to the Service and Terms</H2>
            <P>
              We may modify, suspend, or discontinue any feature or aspect of the Service at any time.
              We will make reasonable efforts to notify users of significant changes.
            </P>
            <P mb={0}>
              We reserve the right to update these Terms at any time. For material changes — including
              changes that affect your rights, obligations, or how we use your data — we will provide at
              least 30 days notice via email or a prominent notice within the platform. For non-material
              changes, the updated Terms take effect immediately upon posting. Continued use of the
              Service after the effective date of any change constitutes your acceptance of the updated
              Terms. Disputes arising under these Terms will be resolved in accordance with the version
              of the Terms that was in effect at the time the dispute arose.
            </P>
          </Section>

          <Section>
            <H2 n={10} id="t10">Disclaimers and limitation of liability</H2>
            <P>
              The Service is provided "as is" and "as available" without warranties of any kind, whether
              express or implied, including but not limited to implied warranties of merchantability,
              fitness for a particular purpose, or non-infringement. We do not warrant that the Service
              will be uninterrupted, error-free, or free from harmful components.
            </P>
            <P>
              To the fullest extent permitted by applicable law, Castle Atlas and its operators shall not
              be liable for any indirect, incidental, special, consequential, or punitive damages arising
              out of or related to your use of or inability to use the Service, including but not limited
              to loss of data, revenue, profits, or goodwill, even if we have been advised of the
              possibility of such damages.
            </P>
            <P mb={0}>
              Our total liability to you for all claims arising out of or relating to these Terms or the
              Service is limited to the greater of: (a) the amount you have paid to Castle Atlas for the
              Service in the 12 months preceding the claim, or (b) $100 USD.
            </P>
          </Section>

          <Section>
            <H2 n={11} id="t11">Indemnity</H2>
            <P mb={0}>
              You agree to defend, indemnify, and hold harmless Castle Atlas and its operators from and
              against any claims, liabilities, damages, losses, and expenses (including reasonable
              attorneys' fees) arising out of or in any way connected with: (a) your access to or use of
              the Service; (b) your violation of these Terms; (c) your violation of any third-party right,
              including any intellectual property, privacy, or publicity right; or (d) any dispute between
              you and any third party. We reserve the right to assume the exclusive defense and control
              of any matter subject to indemnification by you, in which case you agree to cooperate with
              our defense.
            </P>
          </Section>

          <Section>
            <H2 n={12} id="t12">Dispute resolution</H2>
            <P>
              We prefer to resolve disputes informally. If you have a concern, please contact us first at{' '}
              <Mail/> and we will make a good-faith effort to resolve it within 30 days.
            </P>
            <P>
              If a dispute cannot be resolved informally, you and Castle Atlas agree to resolve it through
              binding individual arbitration administered by the American Arbitration Association under its
              Consumer Arbitration Rules, rather than in court. You waive any right to participate in a
              class action lawsuit or class-wide arbitration. The arbitrator may award the same damages
              and relief as a court. Judgment on the award may be entered in any court having jurisdiction.
            </P>
            <P>
              Exceptions: Either party may bring an individual action in small claims court, seek
              injunctive relief in a court of law to stop unauthorized use or infringement of intellectual
              property rights, or pursue an enforcement action through a government agency.
            </P>
            <P mb={0}>
              Opt-out: If you do not wish to resolve disputes by binding arbitration, you may opt out
              by emailing us at <Mail/> within 30 days of first accepting these Terms with your full name,
              email address, and a clear statement that you wish to opt out of arbitration. If you opt out,
              disputes will be resolved in accordance with Section 16 below.
            </P>
          </Section>

          <Section>
            <H2 n={13} id="t13">Copyright and DMCA policy</H2>
            <P>
              Castle Atlas respects the intellectual property rights of others and expects users to do
              the same. If you believe that content on the Service infringes your copyright, please send
              a notice to our designated agent at <Mail/> containing the following:
            </P>
            <List items={[
              'A physical or electronic signature of the person authorized to act on behalf of the copyright owner',
              'A description of the copyrighted work you claim has been infringed',
              'A description of where the infringing material is located on the Service',
              'Your address, telephone number, and email address',
              'A statement that you have a good faith belief that the use is not authorized by the copyright owner or the law',
              'A statement, under penalty of perjury, that the information in your notice is accurate and that you are the copyright owner or authorized to act on their behalf',
            ]}/>
            <P mb={0}>
              If you receive a notice that your content has been removed and believe it was a mistake,
              you may submit a counter-notification with the same contact information above. We will
              terminate the accounts of repeat infringers in appropriate circumstances.
            </P>
          </Section>

          <Section>
            <H2 n={14} id="t14">Termination</H2>
            <P>
              You may delete your account at any time from Settings → Account. Upon deletion, your access
              to the Service terminates immediately and your personal data will be permanently deleted
              within 30 days, subject to any retention obligations required by law.
            </P>
            <P mb={0}>
              We reserve the right to suspend or terminate your account without notice if we determine,
              in our sole discretion, that you have violated these Terms, engaged in conduct harmful to
              other users or the platform, or posed a security risk. Provisions that by their nature
              should survive termination — including Sections 4, 5, 10, 11, 12, 13, and 15 — will remain
              in effect.
            </P>
          </Section>

          <Section>
            <H2 n={15} id="t15">General terms</H2>
            <P>
              These Terms, together with our Privacy Policy and any other agreements expressly
              incorporated by reference, constitute the entire agreement between you and Castle Atlas
              regarding your use of the Service. If any provision of these Terms is held to be invalid
              or unenforceable, that provision will be enforced to the maximum extent permissible and
              the remaining provisions will remain in full force.
            </P>
            <P>
              You may not assign or transfer these Terms without our prior written consent. We may
              assign these Terms at any time without notice. Our failure to enforce any provision does
              not waive our right to enforce it later. The word "including" means "including but not
              limited to."
            </P>
            <P mb={0}>
              By using the Service, you consent to receiving electronic communications from us. You agree
              that any notices, agreements, disclosures, or other communications we send you electronically
              satisfy any legal requirement that such communications be in writing.
            </P>
          </Section>

          <Section>
            <H2 n={16} id="t16">Governing law</H2>
            <P mb={0}>
              These Terms are governed by and construed in accordance with applicable international
              commercial law principles. Disputes not subject to arbitration under Section 12 shall be
              resolved in the courts of competent jurisdiction. We are actively establishing our formal
              legal jurisdiction and will update this section accordingly.
            </P>
          </Section>

          <Section>
            <H2 n={17} id="t17">Contact</H2>
            <P mb={0}>
              Questions about these Terms? Email us at <Mail/>. We read every message.
            </P>
          </Section>

        </div>
      </div>
    </div>
  );
};

export default Terms;
